import React, { useEffect, useState } from "react";
import { Box, Stack } from "@mui/material";
import dayjs from "dayjs";

import { downloadUtilityBillsCSV } from "api";
import { useUtilityBillStatusesMultiselect, useUtilityTypesMultiselect } from "hooks";
import { ExportButton, InfoLabel } from "components";

import { type FilteredParamsType } from "./utils";

export interface ExportButtonWrapperProps {
  tableData: any;
  queryParams: any;
  startDate: string;
  endDate: string;
  /** Optional callback when export filters change (for resetting pagination) */
  onFilterChange?: () => void;
}

export const ExportButtonWrapper = ({
  tableData,
  queryParams,
  startDate,
  endDate,
  onFilterChange,
}: ExportButtonWrapperProps) => {
  const [allowExportCSV, setAllowExportCSV] = useState<boolean>(false);

  // These multiselects are for export filtering only, not table filtering
  // withQueryParams: false means they won't modify URL query params
  const { utilityTypeIds } = useUtilityTypesMultiselect({
    updateExternalStates: onFilterChange,
    withQueryParams: false,
    withEmptyOption: true,
  });

  const { utilityBillStatusesValues } = useUtilityBillStatusesMultiselect({
    onSelectionChange: onFilterChange,
  });

  const {
    direction,
    page,
    size,
    sortColumn,
    areDuplicates,
    startDate: queryStartDate,
    endDate: queryEndDate,
    customerSiteId,
    businessUnit,
    siteName,
    provider,
    accountNumber,
    measuredValue,
    meterNumber,
    scope,
    addedBy,
    type,
    co2Emission,
    amount,
    dataEntriesStatus,
    ...restQueryParams
  } = queryParams;

  const filteredParams: FilteredParamsType = {
    ...restQueryParams,
    areDuplicates,
    startDate: queryStartDate,
    endDate: queryEndDate,
    customerSiteId,
    businessUnit,
    siteName,
    provider,
    accountNumber,
    measuredValue,
    meterNumber,
    scope,
    addedBy,
    type,
    co2Emission,
    amount,
    dataEntriesStatus,
    statuses: utilityBillStatusesValues,
    utilityTypeIds,
  };

  useEffect(() => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    if (!start.isValid() || !end.isValid() || end.isBefore(start)) {
      setAllowExportCSV(false);
      return;
    }

    const diffMonths = Math.abs(end.diff(start, "month", true));
    setAllowExportCSV(diffMonths <= 12);
  }, [startDate, endDate]);

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center">
        <ExportButton
          exportFunction={downloadUtilityBillsCSV}
          exportParams={filteredParams}
          exportTitle="Document_Summary"
          exportText="Export CSV"
          exportingText="Loading CSV..."
          successMessage="CSV exported successfully."
          errorMessage="Error fetching data. Please try again."
          fileExtension=".csv"
          isZipFile={false}
          disabled={!tableData.length || !allowExportCSV}
          sx={{
            borderRadius: "8px",
            width: '200px',
          }}
        />
        <InfoLabel
          infoMessage="Export limited to 12 months of data. Please adjust your date range if needed."
          labelSx={{
            backgroundColor: "transparent",
            padding: 0,
            minWidth: 'auto',
            "& svg": {
              width: "20px",
              height: "20px",
            }
          }}
        />
      </Stack>
    </Box>
  )
};
